import { handleSessionExpiration } from "common/helpers";
import { PERMISSIONS } from "config";
import { StatusCodes } from "http-status-codes";
import * as cache from "memory-cache";

import { Catalinav2BaseService } from "./catalinav2-base-service";

const PERMISSIONS_CACHE_KEY = "PERMISSIONS";
const PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS = 20 * 60 * 60 * 1000; // 20 hours

/**
 * Implements user Validation, Authorization and Authentication.
 */
export class UserPermissions extends Catalinav2BaseService {
  /**
   * Singleton instance with the current user permissions.
   */
  public static current = new UserPermissions();

  private _username: string | null = null;
  private _isRefreshing: boolean = false;
  private _isReauthorizing: boolean = false;

  public get isAuth(): boolean {
    return !!this._username;
  }

  /**
   * Retrieves the username of the currently authenticated user.
   */
  public get username(): string | null {
    return this._username;
  }

  /**
   * Retrieves the list of permissions authorized to the current user.
   */
  public get permissions(): PERMISSIONS[] {
    const permissions = cache.get(PERMISSIONS_CACHE_KEY) as PERMISSIONS[];
    if (!permissions) {
      // If we haven't been through initial authorization yet, throw error for AuthLoading to catch
      if (!this.hasAuthorized) {
        throw new Error("The user has not been authorized.");
      }

      // Permissions have expired during runtime - redirect immediately and refresh in background
      if (!this._isReauthorizing) {
        this._isReauthorizing = true; // Prevent multiple redirects
        handleSessionExpiration();

        // Start background refresh
        if (!this._isRefreshing) {
          this._isRefreshing = true;
          this.authorize()
            .then(() => {
              this._isRefreshing = false;
              this._isReauthorizing = false;
            })
            .catch((error) => {
              console.error("Failed to refresh permissions in background:", error);
              this._isRefreshing = false;
            });
        }
      }

      // Return empty array for security when permissions are expired
      return [];
    }

    return permissions;
  }

  /**
   * Identifies whether or not the current used has been through the authorization process, irrespective of the result.
   * @returns True if the user has been through the authorization process.
   */
  public get hasAuthorized(): boolean {
    return cache.get(PERMISSIONS_CACHE_KEY) != null;
  }

  /**
   * Identifies whether or not the current user has a given permission.
   * @param permission The permission to check.
   * @returns True if the user has the specified permission.
   */
  public async hasPermissionAsync(permission: PERMISSIONS): Promise<boolean> {
    await this.authorize();
    return this.hasPermission(permission);
  }

  /**
   * Identifies whether or not the current user has a given permission.
   * @param permission The permission to check.
   * @returns True if the user has the specified permission.
   */
  public hasPermission(permission: PERMISSIONS): boolean {
    if (!this.isAuth) {
      return false;
    }

    const permissions = cache.get(PERMISSIONS_CACHE_KEY) as PERMISSIONS[];

    if (!permissions) {
      // Permissions have expired - redirect immediately and refresh in background
      if (!this._isReauthorizing) {
        this._isReauthorizing = true; // Prevent multiple redirects
        handleSessionExpiration();

        // Start background refresh
        if (!this._isRefreshing) {
          this._isRefreshing = true;
          this.authorize()
            .then(() => {
              this._isRefreshing = false;
              this._isReauthorizing = false;
            })
            .catch((error) => {
              console.error("Failed to refresh permissions in background:", error);
              this._isRefreshing = false;
              // Keep _isReauthorizing true to prevent further attempts
            });
        }
      }

      // Always deny access for security when permissions are expired
      return false;
    }

    // Reset flags when we have valid permissions
    this._isReauthorizing = false;
    this._isRefreshing = false;
    return permissions.includes(permission);
  }

  /**
   * Executes authorization (and indirectly authentication) for the current user.
   */
  public async authorize() {
    if (!cache.get(PERMISSIONS_CACHE_KEY)) {
      const permissions = await this.callPermissionsService();
      cache.put(PERMISSIONS_CACHE_KEY, permissions, PERMISSIONS_CACHE_EXPIRATION_IN_MILLISECONDS);
    }
  }

  private async callPermissionsService(): Promise<PERMISSIONS[]> {
    const response = await this.fetch("/user-permissions");
    if (response.status !== StatusCodes.OK) {
      throw new Error("An error occurred when authorizing the user.");
    }
    const jsonResponse = await response.json();
    if (jsonResponse.failureReason) {
      throw new Error(jsonResponse.failureReason);
    }
    this._username = jsonResponse.username;
    return jsonResponse.allowedFeatures as PERMISSIONS[];
  }
}
